import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ** MUI Components
import { Avatar, Box, Typography, Tooltip, AvatarGroup } from "@mui/material";

// ** Custom Component Imports
import CustomChip from "../../../components/mui/chip";

// ** Icon Imports
import Icon from "../../../components/icon";

// ** Logic Imports
import { GetServiceRequestRaw, GetServiceRequests } from "screens/Requests/logic/GetServiceRequest";
import { SaveServiceRequest } from "screens/Requests/logic/SaveServiceRequest";
import { useAssessmentPermission } from "../permissions/AssessmentPermissions";

export const useIndexLogic = () => {
  //#region Permissions
  const { AssessmentPermissionProps } = useAssessmentPermission();
  const {
    hasAssessmentGetPermission,
    hasAssessmentSavePermission,
  } = AssessmentPermissionProps;
  //#endregion Permissions

  //#region Hooks
  const navigate = useNavigate();
  //#endregion

  //#region Constants
  const contentHeightFixed = false;
  //#endregion End of Constants

  //#region States

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [paginationText, setPaginationText] = useState('');
  const [openNewAssessmentDlg, setOpenNewAssessmentDlg] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openTagManagementDlg, setOpenTagManagementDlg] = useState(false);
  const [openScheduleAst , setOpenScheduleAst] = useState(false);

  const tagManagementData = { title: "Tag Management" };

  const extractId = (input) => {
    return input.replace(/\D/g, '');
  };
  
  //#endregion end of states

  //#region Handlers and methods

  useEffect(() => {
    fetchAssessments();
  }, [hasAssessmentGetPermission]);

  useEffect(() => {
    if(!hasAssessmentGetPermission) return;

    setFilteredRows(() =>
      data.filter((row) =>
        Object.values(row).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchText.toLowerCase())
        )
      )
    );
  }, [searchText]);

  useEffect(() => {
    const total = filteredRows.length;
    let start = paginationModel.page * paginationModel.pageSize + 1;
    let end = Math.min((paginationModel.page + 1) * paginationModel.pageSize, total);
  
    if (total === 0) {
      setPaginationText("0-0 of 0");
      return;
    }

    if (paginationModel.pageSize === -1) {
      start = 1;
      end = total;
    }
  
    setPaginationText(`${end} of ${total}`);
  }, [filteredRows, paginationModel.page, paginationModel.pageSize]);

  const fetchAssessments = async () => {
    if(!hasAssessmentGetPermission) return;

    try {
      setLoading(true);
      // console.log(loading)
      let requests = await GetServiceRequests(0, 0, 2); // entityType 2 for Assessments
  
    const assessments = requests
  .filter(x => x.entityType.toLowerCase() === "assessment")
  .sort((a, b) => {
    const dateDiff = new Date(b.requestedOn) - new Date(a.requestedOn);

    if (dateDiff !== 0) {
      return dateDiff; // sort by requestedOn first
    }

    // fallback: use createDate if requestedOn is same
    if (a.createDate && b.createDate) {
      return new Date(b.createDate) - new Date(a.createDate);
    }

    // last fallback: use requestID
    return (b.requestID || 0) - (a.requestID || 0);
  });

      setData(assessments);
      setFilteredRows(assessments);
    } catch (error) {
      console.error("Error fetching Requests data:", error);
    } finally {
      // console.log(loading)
      setLoading(false);
    }
  };
  

  const handleOpenNewAssessmentDlg = () => {
    setOpenNewAssessmentDlg(true);
  };
  const handleCloseNewAssessmentDlg = () => {
    setOpenNewAssessmentDlg(false);
    fetchAssessments();
  };

  const handleOpenScheduleAstDlg = () => {
    setOpenScheduleAst(true);
  }

  const handleCloseScheduleAstDlg = () => {
    setOpenScheduleAst(false);
  }

  const initialServiceRequestState = {
    requestID: 0,
    requestTitle: null,
    reqDescription: null,
    completeWorkBy: null,
    customerID: 0,
    jobSiteID: 0,
    jobsiteContactID: 0,
    requestSource: null,
    status: null,
    availabilityTime: [],
    availabilityDays: [],
    tags: [],
    assignedToUsers: []
  }
  const [serviceRequest, setServiceRequest] = useState(initialServiceRequestState);
  const [Tag, setTag]  = useState('');

  const handleCloseTagManagementDlg = () => setOpenTagManagementDlg(false);
  const handleOpenTagManagementDlg = (tags, id, requestTitle, status) => {
    const formattedTags = tags.map(tag => ({ tagName: tag }));
    setServiceRequest((prev) => {
      return { ...prev, requestID: id, requestTitle: requestTitle, tags: formattedTags, status: status };
    })
    setOpenTagManagementDlg(true);
  }

  const handleAddTag = async (tagName) => {
    if(!hasAssessmentSavePermission) return;
    
    if (tagName.trim() === '') return;
  
    const lowerCaseTagName = tagName.toLowerCase();
  
    const tagExists = serviceRequest.tags?.some(
      (tag) => tag.tagName.toLowerCase() === lowerCaseTagName
    );
  
    if (tagExists) return;
  
    const updatedTags = [...(serviceRequest.tags || []), { tagName }];
    const updatedRequest = {
      requestID: serviceRequest.requestID,
      requestTitle: serviceRequest.requestTitle,
      status: serviceRequest.status,
      tags: updatedTags,
    };
  
    setServiceRequest(updatedRequest);
    setTag('');
  
    try {
      await SaveServiceRequest({ requestID: serviceRequest.requestID, requestTitle: serviceRequest.requestTitle, tags: updatedTags, status: serviceRequest.status }, navigate, 'Assessment');
      await fetchAssessments();
    } catch (error) {
      console.error('Error saving tag:', error);
    }
  };

  const handleDeleteTag = async (tagName) => {
    if(!hasAssessmentSavePermission) return;

    const updatedTags = serviceRequest.tags.filter(tag => tag.tagName !== tagName);
  
    const updatedRequest = {
      requestID: serviceRequest.requestID,
      requestTitle: serviceRequest.requestTitle,
      status: serviceRequest.status,
      tags: updatedTags,
    };
  
    setServiceRequest(updatedRequest);
  
    try {
      await SaveServiceRequest({
        requestID: serviceRequest.requestID,
        requestTitle: serviceRequest.requestTitle,
        status: serviceRequest.status,
        tags: updatedTags,
      }, navigate, 'Assessment');
  
      await fetchAssessments();
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  };

  const handleDeleteTagByID = async (tagName, tags, id, requestTitle, status) => {
    if(!hasAssessmentSavePermission) return;

    const updatedTags = tags.filter(tag => tag !== tagName);
    const formattedTags = updatedTags.map(tag => ({ tagName: tag }));
  
    const updatedRequest = {
      requestID: id,
      requestTitle: requestTitle,
      status: status,
      tags: formattedTags,
    };
  
    setServiceRequest(updatedRequest);
  
    try {
      await SaveServiceRequest({
        requestID: id,
        requestTitle: requestTitle,
        status: status,
        tags: formattedTags,
      }, navigate, 'Assessment');
  
      await fetchAssessments();
      setServiceRequest({});
    } catch (error) {
      console.error('Error deleting tag by ID:', error);
    }
  };

  const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '';
    
    return name
      .split(' ')
      .filter(part => part.trim().length > 0)
      .map(part => part[0].toUpperCase())
      .join('');
  };

  const columns = [
    {
      // flex: 0.1,
      field: "id",
      minWidth: 100,
      headerName: "ID",
      renderCell: ({ row }) => {
        const { id } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate(`/assessments/detail/${extractId(id)}`)}
          >
            <Typography sx={{ flexGrow: 1 }}>{id}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "requestTitle",
      minWidth: 190,
      headerName: "Assessment Title",
      renderCell: ({ row }) => {
        const { id, requestTitle } = row;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              cursor: "pointer",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            onClick={() => navigate(`/assessments/detail/${extractId(id)}`)}
          >
            <Typography sx={{ flexGrow: 1 }}>{requestTitle}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "customerName",
      minWidth: 160,
      headerName: "Customer Name",
      renderCell: ({ row }) => {
        const { customerName ,customerID} = row;

        return (
          <Box sx={{ display: "flex", 
            alignItems: "center", 
            height: "100%" ,
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
          }, }} 
          onClick={() => navigate(`/customers/detail/${customerID}`)}>
            <Typography sx={{ flexGrow: 1 }}>{customerName}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "jobsite",
      headerName: "Jobsite",
      minWidth: 150,
      renderCell: (params) => {
        const { row } = params;

        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: "100%", 
            }}
          >
            <Typography
              noWrap
              variant="body2"
              sx={{ color: "text.primary", fontWeight: 600 }}
            >
              {row.jobsiteName}
            </Typography>
            <Typography noWrap color="text.secondary" fontSize="small">
              {row.jobsiteAddress}
            </Typography>
          </Box>
        );
      },
    },
    {
     // flex: 0.1,
      field: "createdOn",
      headerName: "Created On",
      minWidth: 140,
      renderCell: ({ row }) => {
        const { requestedOn } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{requestedOn}</Typography>
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "createBy",
      headerName: "Created By",
      minWidth: 140,
      renderCell: ({ row }) => {
        const { createBy } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Typography sx={{ flexGrow: 1 }}>{createBy}</Typography>
          </Box>
        );
      },
    },
    {
  // flex: 0.1,
  field: "convertedfrom",
  headerName: "Converted From",
  minWidth: 170,
  renderCell: ({ row }) => {
    const { convertedfrom, requestID } = row;
    const hasRequest = convertedfrom !== 0;
    
    return (
      <Box 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          height: "100%",
          cursor: hasRequest ? 'pointer' : 'default',
          '&:hover': {
            textDecoration: hasRequest ? 'underline' : 'none'
          },
        }} 
        onClick={hasRequest ? () => navigate(`/requests/detail/${convertedfrom}`) : undefined}
      >
        <Typography sx={{ flexGrow: 1 }}>
          {hasRequest ? `REQ-${convertedfrom}` : "-"}
        </Typography>
      </Box>
    );
  },
},
    {
      // flex: 0.1,
      field: "assignedTo",
      headerName: "Assigned To",
      minWidth: 140,
      renderCell: ({ row }) => {
        const { assignedTo } = row;

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {assignedTo && assignedTo.length > 0 ? (
              <AvatarGroup
                className="pull-up"
                max={4}
                sx={{
                  "& .MuiAvatar-root": { width: 25, height: 25 },
                  "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
                }}
              >
                {assignedTo.map((assignee) => (
                  <Tooltip title={assignee.userName}>
                    <Avatar
                      src="/images/avatars/4.png"
                      alt="employee-picture"
                      sx={{ width: 25, height: 25, fontSize: '12px' }}
                    >{getInitials(assignee.userName)}</Avatar>
                  </Tooltip>
                ))}
              </AvatarGroup>
            ) : (
              <Typography variant='body2' color='text.secondary'>Unassigned</Typography>
            )}
          </Box>
        );
      },
    },
    {
      // flex: 0.1,
      field: "status",
      headerName: "Status",
      minWidth: 120,
      renderCell: ({ row }) => {
        const { status } = row;

        return (
          <CustomChip
            rounded
            size="small"
            skin="light"
            sx={{ lineHeight: 1 }}
            color={status === "Under Review" ? "info" : status === "Submitted" || status === "On-Site Scheduled" || status === "Phone Scheduled" || status === "Rescheduled" || status === 'Completed'  ? "success" : status === "On Hold" ? "warning" : status === "Rejected" || status === "Closed"  ? "error" :  "secondary"}
            label={status}
          />
        );
      },
    },
    {
      // flex: 0.1,
      field: "tags",
      minWidth: 300,
      headerName: "Tags",
      renderCell: ({ row }) => {
        const { tags, requestID, requestTitle, status } = row;

        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexWrap: "nowrap",
              alignItems: "center",
              height: "100%",
            }}
          >
            <CustomChip
              rounded
              label="Add"
              size="small"
              color="info"
              skin="light"
              sx={{ lineHeight: 1 }}
              onDelete={() => handleOpenTagManagementDlg(tags, requestID, requestTitle, status)}
              deleteIcon={<Icon icon="tabler:circle-plus" />}
            />

            {tags && tags.length > 0 ? (
              tags.length <= 2 ? (
                tags.map((tag, index) => (
                  <CustomChip
                    key={index}
                    rounded
                    label={tag}
                    size="small"
                    color="info"
                    skin="light"
                    sx={{ lineHeight: 1 }}
                    onDelete={() => handleDeleteTagByID(tag, tags, requestID, requestTitle, status)}
                  />
                ))
              ) : (
                <>
                  <CustomChip
                    rounded
                    label={tags[0]}
                    size="small"
                    color="info"
                    skin="light"
                    sx={{ lineHeight: 1 }}
                    onDelete={() => handleDeleteTagByID(tags[0], tags, requestID, requestTitle, status)}
                  />
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{ display: "inline-flex", alignItems: "center" }}
                  >
                    ...
                  </Typography>
                  <CustomChip
                    rounded
                    label="See More"
                    size="small"
                    color="info"
                    skin="light"
                    sx={{
                      lineHeight: 1,
                      "&:hover": {
                        backgroundColor: "rgba(0, 58, 112, 0.16)",
                      },
                    }}
                    onClick={() => handleOpenTagManagementDlg(tags, requestID, requestTitle, status)}
                  />
                </>
              )
            ) : (
              <Typography variant="body2" color="textSecondary">
                No tags
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  const handleClearSelection = () => {
    setRowSelectionModel([]);
    setSelectedRows(0);
  };

  const handleGridSelection = (selectedRows) => setSelectedRows(selectedRows);

  const handleSearch = (val) => setSearchText(val);

  const handleClearSearch = () => setSearchText("");

  //#endregion end of handlers and methods

  const IndexProps = {
    columns,
    contentHeightFixed,
    handleOpenNewAssessmentDlg,
    handleCloseNewAssessmentDlg,
    handleOpenTagManagementDlg,
    handleCloseTagManagementDlg,
    handleClearSelection,
    handleGridSelection,
    handleSearch,
    handleClearSearch,
    loading,
    setLoading,
    filteredRows,
    searchText,
    data,
    paginationText,
    paginationModel,
    setPaginationModel,
    openNewAssessmentDlg,
    selectedRows,
    rowSelectionModel,
    setRowSelectionModel,
    openTagManagementDlg,
    tagManagementData,
    fetchAssessments,
    handleAddTag,
    handleDeleteTag,
    Tag,
    setTag,
    serviceRequest
  };

  return {
    IndexProps,
  };
};
